import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Badge } from '@vaiu/ui/badge';
import { Eye, EyeOff, Copy, CheckCircle, AlertCircle, Clock, Wifi, WifiOff, Smartphone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { boPaymentsApi } from '@/services/platformApi';
import VaiuLogo from '../shared/VaiuLogo';
import { CurrencyDisplay } from '@vaiu/ui';
import { currencyService } from '@/services/currencyService';

interface CardData {
  cardNumber: string | number;
  cardExpMth: number;
  cardExpYear: number;
  cardCvv: number;
  cardHolderName: string;
  tokenId: string;
  uniqueUserId: string;
  status: string;
  amountLoaded?: number;
  currency?: string;
  cardCurrency?: string;
  createdAt?: string;
  convertedAt?: string;
  cardBrand?: 'visa' | 'mastercard';
}

interface PrepaidCardProps {
  money: {
    amount: string | number;
    currency: string;
  };
  display?: 'card' | 'last4' | 'full';
  status?: 'activated' | 'failed' | 'pending' | string;
  cardCreatedCallback?: (result: any) => void;
  tokenKey?: string;
  autoStart?: boolean;
  className?: string;
  existingCardData?: CardData;
  cardBrand?: 'visa' | 'mastercard';
  tokenCreatedAt?: string; // New prop for token creation time
  tokenExpiry?: string; // New prop for token expiry time
  showNfcButton?: boolean;
  onNfcPayClick?: () => void;
}

const PrepaidCard: React.FC<PrepaidCardProps> = ({ 
  money, 
  display = 'card', 
  status,
  cardCreatedCallback,
  tokenKey,
  autoStart = false,
  className = '',
  existingCardData,
  cardBrand = 'visa',
  tokenCreatedAt,
  tokenExpiry,
  showNfcButton = false,
  onNfcPayClick
}) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [isVppcDisabled, setIsVppcDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cardData, setCardData] = useState<CardData | null>(existingCardData || null);
  const [showCvv, setShowCvv] = useState<boolean>(false);
  const [hideCard, setHideCard] = useState<boolean>(false);
  const { toast } = useToast();

  // Request deduplication - prevent multiple simultaneous card creation requests
  const activeRequestRef = useRef<Promise<any> | null>(null);
  const hasAttemptedRef = useRef<boolean>(false);

  // Auto-start conversion if autoStart is true and we have a tokenKey
  useEffect(() => {
    if (autoStart && tokenKey && !cardData && !isLoading && !hasAttemptedRef.current) {
      hasAttemptedRef.current = true; // Mark as attempted to prevent retries
      handleCreateCard();
    }
  }, [autoStart, tokenKey, cardData, isLoading]);

  // Helper function to format remaining time to expiration
  const formatTimeRemaining = (expiryDateString: string): string => {
    if (!expiryDateString) return 'N/A';
    
    try {
      const now = new Date();
      const expiry = new Date(expiryDateString);
      const diff = expiry.getTime() - now.getTime();
      
      if (diff <= 0) return 'Expired';
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      return `${days.toString().padStart(2, '0')}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to format creation date
  const formatCreationDate = (createdAtString: string): string => {
    if (!createdAtString) return 'N/A';
    
    try {
      const date = new Date(createdAtString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const handleCreateCard = async () => {
    if (!tokenKey) {
      setIsError(true);
      toast({
        title: "Error", 
        description: "No token key provided for conversion",
        variant: "destructive"
      });
      return;
    }

    if (activeRequestRef.current) return;
    activeRequestRef.current = new Promise(async (resolve, reject) => {
      setIsLoading(true);
      setIsError(false);
      setIsVppcDisabled(false);

      try {
        const result = await boPaymentsApi.convertTokenToPrepaidCard(tokenKey);
        
        // Backend returns card data directly, not wrapped in cardData property
        if (result && (result.cardData || result.cardNumber)) {
          const cardData = result.cardData || result; // Handle both formats
          const processedCardData = {
            ...cardData,
            cardBrand: cardBrand
          };
          setCardData(processedCardData);
          toast({
            title: "Success! 🎉", 
            description: `Token converted to ${cardBrand.toUpperCase()} prepaid card successfully`
          });
          
          if (cardCreatedCallback) {
            cardCreatedCallback({
              cardData: processedCardData
            });
          }
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error: any) {
        console.error('Error converting token to prepaid card:', error);
        
        // Check if this is specifically a VPPC disabled error
        const errorMessage = error.response?.data?.error || error.message || "";
        const isVppcDisabledError = errorMessage.includes('vppc-disabled') || errorMessage.includes('VPPC') && errorMessage.includes('disabled');
        
        if (isVppcDisabledError) {
          setIsVppcDisabled(true);
          toast({
            title: "Card Issuance Disabled", 
            description: "Prepaid card issuance is currently disabled for this platform",
            variant: "destructive"
          });
        } else {
          setIsError(true);
          toast({
            title: "Conversion Failed", 
            description: errorMessage || "Failed to convert token to prepaid card",
            variant: "destructive"
          });
        }
      } finally {
        setIsLoading(false);
        activeRequestRef.current = null;
        resolve(null);
      }
    });
  };

  // Helper function to format card number into 4x4 grid
  const formatCardNumberGrid = (cardNumber: string | number): string[][] => {
    const numStr = cardNumber.toString().padStart(16, '0');
    const grid = [];
    for (let i = 0; i < 4; i++) {
      grid.push(numStr.substr(i * 4, 4).split(''));
    }
    return grid;
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!", 
        description: `${label} copied to clipboard`
      });
    } catch (error) {
      toast({
        title: "Copy Failed", 
        description: "Could not copy to clipboard",
        variant: "destructive"
      });
    }
  };

  // Get card brand styling and logo
  const getCardBrandInfo = (brand?: string) => {
    const cardBrandName = brand || cardBrand;
    switch (cardBrandName?.toLowerCase()) {
      case 'mastercard':
        return {
          name: 'MASTERCARD',
          gradient: 'from-gray-900 to-black',
          logo: (
            <div className="flex items-center space-x-1">
              <div className="w-6 h-6 bg-red-500 rounded-full opacity-80"></div>
              <div className="w-6 h-6 bg-yellow-400 rounded-full opacity-80 -ml-4"></div>
            </div>
          )
        };
      case 'visa':
      default:
        return {
          name: 'VISA',
          gradient: 'from-gray-900 to-black',
          logo: (
            <div className="text-white text-lg font-bold bg-white/20 px-3 py-1 rounded backdrop-blur-sm">
              VISA
            </div>
          )
        };
    }
  };

  // Show loading state during conversion
  if (isLoading) {
    return (
      <Card className={`w-full max-w-md ${className}`}>
        <CardContent className="p-6">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600">Converting token to {cardBrand.toUpperCase()} prepaid card...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show VPPC disabled state
  if (isVppcDisabled && !cardData) {
    return (
      <Card className={`w-full max-w-md border-orange-200 ${className}`}>
        <CardContent className="p-6">
          <div className="flex flex-col items-center space-y-4">
            <AlertCircle className="h-8 w-8 text-orange-500" />
            <div className="text-center">
              <p className="text-sm text-orange-600 font-medium">Card Issuance Disabled</p>
              <p className="text-xs text-orange-500 mt-1">Prepaid card functionality is currently disabled for this platform</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state (for other errors)
  if (isError && !cardData) {
    return (
      <Card className={`w-full max-w-md border-red-200 ${className}`}>
        <CardContent className="p-6">
          <div className="flex flex-col items-center space-y-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <p className="text-sm text-red-600 text-center">Failed to create prepaid card</p>
            {tokenKey && (
              <Button 
                onClick={handleCreateCard} 
                variant="outline" 
                size="sm"
                disabled={isLoading || !!activeRequestRef.current}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                {isLoading ? 'Creating...' : 'Try Again'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no card data and not autoStart, show create button
  if (!cardData && !autoStart && tokenKey) {
    return (
      <Card className={`w-full max-w-md ${className}`}>
        <CardContent className="p-1">
          <div className="flex flex-col items-center space-y-1">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900">Issue {cardBrand.toUpperCase()} Prepaid Card</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Convert your <CurrencyDisplay amount={parseFloat(money.amount.toString())} currency={money.currency} currencyService={currencyService} /> token to a {cardBrand.toUpperCase()} prepaid card
              </p>
            </div>
            <Button 
              onClick={handleCreateCard}
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : `Issue ${cardBrand.toUpperCase()} Prepaid Card`}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If still no card data, show placeholder
  if (!cardData) {
    return (
      <Card className={`w-full max-w-md ${className}`}>
        <CardContent className="p-1">
          <div className="text-center text-gray-500">
            <Clock className="h-8 w-8 mx-auto mb-0.5" />
            <p className="text-sm">Prepaid card information not available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Main card display
  if (hideCard) {
    return (
      <Card className={`w-full max-w-md ${className}`}>
        <CardContent className="p-1">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-600">
              Your {getCardBrandInfo(cardData.cardBrand).name} Prepaid Card
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setHideCard(false)}
            >
              Show Card
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const cardNumberGrid = formatCardNumberGrid(cardData.cardNumber);
  const expiryDate = `${cardData.cardExpMth.toString().padStart(2, '0')}/${cardData.cardExpYear}`;
  const brandInfo = getCardBrandInfo(cardData.cardBrand);

  return (
    <div className={`w-full ${className}`}>
      <div className="space-y-0.5">
        
        {/* Header row (no title). Hide icon will be on the top-right of the card itself */}
        <div className="px-0.5 sm:px-0" />

        {/* Card Display - Dark background overlay for better contrast */}
        <div className={`bg-gradient-to-br ${brandInfo.gradient} rounded-lg p-0.5 text-white relative overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.35)] ring-1 ring-white/10`}>
          {/* Dark overlay for better text contrast */}
          <div className="absolute inset-0 bg-black/60 rounded-lg"></div>
          
          {/* Card Brand Logo - Top Right */}
          <div className="absolute top-0.5 right-0.5 z-10">
            <div className="scale-75">{brandInfo.logo}</div>
          </div>
          
          {/* Vaiu Logo - Top Left */}
          <div className="absolute top-0.5 left-0.5 z-10">
            <VaiuLogo variant="icon" size="sm" />
          </div>
          
          {/* Centered amount */}
          <div className="relative z-10 mb-0.5 mt-1">
            <div className="text-lg font-semibold text-white text-center tracking-wide">
              <CurrencyDisplay amount={parseFloat(money.amount.toString())} currency={money.currency} currencyService={currencyService} />
            </div>
          </div>

          {/* Move No NFC to mid-right */}
          <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
            <div className="relative">
              <Wifi className="h-4 w-4 text-white" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-5 h-0.5 bg-red-400 rotate-45"></div>
              </div>
            </div>
            <span className="text-[10px] text-white font-semibold">No NFC</span>
          </div>

          {/* Credit Card Number in Compact Format */}
          <div className="mb-0.5 relative z-10">
            <div className="font-mono text-base text-white font-bold tracking-widest">
              {cardData.cardNumber.toString().replace(/(.{4})/g, '$1 ').trim()}
            </div>
          </div>

          {/* Card Holder and Expiry */}
          <div className="flex justify-between items-end text-xs relative z-10">
            <div>
              <p className="text-white uppercase tracking-wide font-bold">Card Holder</p>
              <p className="font-bold text-white text-sm">{cardData.cardHolderName}</p>
            </div>
            <div className="text-right">
              <p className="text-white uppercase tracking-wide font-bold">Valid Thru</p>
              <p className="font-mono text-white font-bold">{expiryDate}</p>
            </div>
          </div>
        </div>

        {/* CVV Section */}
        <div className="flex justify-between items-center px-0.5 sm:px-0">
          <div>
            <span className="text-sm font-medium text-gray-700">CVV: </span>
            {showCvv ? (
              <span className="font-mono text-sm font-bold text-purple-700">
                {cardData.cardCvv}
              </span>
            ) : (
              <span className="font-mono text-sm">•••</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCvv(!showCvv)}
          >
            {showCvv ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>

        {/* Action Buttons */}
        <div className={`grid ${showNfcButton ? 'grid-cols-3' : 'grid-cols-2'} gap-0.5 px-0.5 sm:px-0`}>
          {showNfcButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={onNfcPayClick}
              className="flex items-center space-x-0.5 text-xs"
            >
              <Smartphone className="h-3 w-3" />
              <span>NFC Pay</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyToClipboard(cardData.cardNumber.toString(), 'Card number')}
            className="flex items-center space-x-0.5 text-xs"
          >
            <Copy className="h-3 w-3" />
            <span>Copy Number</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyToClipboard(cardData.cardCvv.toString(), 'CVV')}
            className="flex items-center space-x-0.5 text-xs"
          >
            <Copy className="h-3 w-3" />
            <span>Copy CVV</span>
          </Button>
        </div>

        {/* Status Badge */}
        <div className="flex justify-center px-0.5 sm:px-0">
          <Badge 
            variant={cardData.status === 'VIRTUAL_ACTIVE' ? 'default' : 'secondary'}
            className="text-xs"
          >
            {cardData.status === 'VIRTUAL_ACTIVE' ? `✓ ${brandInfo.name} Active` : cardData.status}
          </Badge>
        </div>

        {/* Usage Note */}
        <div className="text-center text-xs text-gray-500 px-0.5 sm:px-0">
          💡 This virtual {brandInfo.name} card can be used for online purchases and digital payments
        </div>

        {/* Token Details */}
        <div className="mt-1 text-xs text-gray-600 px-0.5 sm:px-0">
          <p>Token Created: {formatCreationDate(tokenCreatedAt || '')}</p>
          <p>Time Remaining: {tokenExpiry ? formatTimeRemaining(tokenExpiry) : 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default PrepaidCard; 